/**
 * Availability schedule — a musician's calendar of explicitly-marked blocks
 * (rehearsal, gig, meeting, recording, event, other) shared into group chat
 * so bandmates/collaborators know when to reach them, without needing an
 * external calendar. Manual share only (v1): a slot posted into chat is a
 * snapshot at share-time, not a live/synced booking.
 *
 * All time is considered open/vacant by default — a musician only needs to
 * mark the exceptions: a block of time they're explicitly FREE for (status
 * 'free', confirming a window for a given type) or already BUSY/booked
 * (status 'busy'). A busy block can come from something booked through BB or
 * from a booking picked up elsewhere (source 'external').
 *
 *   availabilitySlots/{id}
 */
import { db } from '@/lib/firebase';
import {
  collection, doc, addDoc, getDocs, updateDoc, deleteDoc,
  query, where, limit, Timestamp,
} from 'firebase/firestore';

export const AVAILABILITY_TYPES = ['rehearsal', 'gig', 'meeting', 'recording', 'event', 'other'] as const;
export type AvailabilityType = typeof AVAILABILITY_TYPES[number];

export const AVAILABILITY_STATUSES = ['free', 'busy'] as const;
export type AvailabilityStatus = typeof AVAILABILITY_STATUSES[number];

export interface AvailabilitySlot {
  id: string;
  uid: string;
  type: AvailabilityType;
  /** Defaults to 'free' when absent — older slots predate this field and were
   *  always "I'm free" entries. */
  status?: AvailabilityStatus;
  /** Only meaningful for busy slots: booked through BB itself, or picked up
   *  elsewhere (a gig booked off-platform) and logged here just to block the
   *  time out. Defaults to 'bb'. */
  source?: 'bb' | 'external';
  /** ISO 8601 datetime strings (local wall-clock, no timezone conversion — BB-native, single-user editing their own slots). */
  startAt: string;
  endAt: string;
  note?: string;
  createdAt?: any;
  updatedAt?: any;
}

/** Read helper — older docs have no `status` field and were always "free". */
export function slotStatus(s: Pick<AvailabilitySlot, 'status'>): AvailabilityStatus {
  return s.status || 'free';
}

const col = () => collection(db, 'availabilitySlots');

export async function createAvailabilitySlot(
  input: Omit<AvailabilitySlot, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const ref = await addDoc(col(), {
    ...input,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });
  return ref.id;
}

export async function updateAvailabilitySlot(
  slotId: string,
  updates: Partial<Omit<AvailabilitySlot, 'id' | 'uid' | 'createdAt'>>,
): Promise<void> {
  await updateDoc(doc(db, 'availabilitySlots', slotId), {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

export async function deleteAvailabilitySlot(slotId: string): Promise<void> {
  await deleteDoc(doc(db, 'availabilitySlots', slotId));
}

/** All of a user's slots (past + future), newest-start first for the manage page. */
export async function getAvailabilitySlots(uid: string): Promise<AvailabilitySlot[]> {
  const snap = await getDocs(query(col(), where('uid', '==', uid), limit(200)));
  return snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<AvailabilitySlot, 'id'>) }))
    .sort((a, b) => a.startAt.localeCompare(b.startAt));
}

/** Only upcoming slots — what the chat "share availability" picker offers. */
export async function getUpcomingAvailabilitySlots(uid: string, max = 20): Promise<AvailabilitySlot[]> {
  const now = Date.now();
  const all = await getDocs(query(col(), where('uid', '==', uid), limit(200)));
  return all.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<AvailabilitySlot, 'id'>) }))
    .filter(s => new Date(s.endAt).getTime() >= now)
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, max);
}
